'use strict';

var _ = require('lodash');

var REPEATER_DELIMITER = '__';
var REPEATER_DELIMITER_LENGTH = REPEATER_DELIMITER.length;
var REFERRED_POINTER_DELIMITER = '_r_';

var getUniqueDisplayedId = function getUniqueDisplayedId(originalId, itemId) {
  return originalId ? originalId + REPEATER_DELIMITER + itemId : undefined;
};

var getUniqueRefId = function getUniqueRefId(refHostId, compId) {
  return "" + refHostId + REFERRED_POINTER_DELIMITER + compId;
};

var updateCompIdInPointersMap = function updateCompIdInPointersMap(_ref, itemId, updatePointerMap) {
  var id = _ref.id;
  return updatePointerMap(id, getUniqueDisplayedId(id, itemId));
};

var isRepeatedComponent = function isRepeatedComponent(compId) {
  if (!_.isString(compId)) {
    return false;
  }

  var delimeterIndex = compId.indexOf(REPEATER_DELIMITER);

  if (delimeterIndex === -1) {
    // `compId` doesn't include `REPEATER_DELIMITER`, so it is not a repeated component
    return false;
  }

  var templateIdWithDelimeterLength = delimeterIndex + REPEATER_DELIMITER_LENGTH;

  if (templateIdWithDelimeterLength === compId.length) {
    // `compId` ends with the first `REPEATER_DELIMITER`,
    // so it has no "repeater item id", so it is not a repeated component
    return false;
  }

  if (compId.indexOf(REPEATER_DELIMITER, templateIdWithDelimeterLength) === templateIdWithDelimeterLength) {
    // `compId` contains two `REPEATER_DELIMITER`s next to each other,
    // so it has no "repeater item id", so it is not a repeated component
    return false;
  }

  return true;
};

var getRepeaterItemId = function getRepeaterItemId(uniqueItemId) {
  return _.isString(uniqueItemId) ? uniqueItemId.split(REPEATER_DELIMITER)[1] : undefined;
};

var getRepeaterTemplateId = function getRepeaterTemplateId(uniqueItemId) {
  return _.isString(uniqueItemId) ? uniqueItemId.split(REPEATER_DELIMITER)[0] : undefined;
};

var isReferredId = function isReferredId(id) {
  return _.includes(id, REFERRED_POINTER_DELIMITER);
};

var isDisplayedOnlyComponent = function isDisplayedOnlyComponent(compId) {
  return isRepeatedComponent(compId) || isReferredId(compId);
};

var getReferredCompId = function getReferredCompId(compId) {
  return isReferredId(compId) ? compId.substring(compId.indexOf(REFERRED_POINTER_DELIMITER) + REFERRED_POINTER_DELIMITER.length) : undefined;
};

var getRefHostCompId = function getRefHostCompId(compId) {
  return isReferredId(compId) ? compId.substring(0, compId.lastIndexOf(REFERRED_POINTER_DELIMITER)) : undefined;
};

var getRootRefHostCompId = function getRootRefHostCompId(compId) {
  return isReferredId(compId) ? compId.split(REFERRED_POINTER_DELIMITER)[0] : undefined;
};

var uniquePropertyMapperFunctions = {
  dataQuery: getUniqueDisplayedId,
  designQuery: getUniqueDisplayedId,
  id: getUniqueDisplayedId,
  parent: getUniqueDisplayedId,
  layout: function layout(value) {
    return _.cloneDeep(value);
  },
  components: function components(value, itemId, updatePointerMap) {
    return _.map(value, function (child) {
      return _.isString(child) ? getUniqueDisplayedId(child, itemId) : getUniqueStructure(child, itemId, updatePointerMap);
    });
  }
};

function getUniqueStructure(structure, itemId, updatePointerMap) {
  if (updatePointerMap === void 0) {
    updatePointerMap = _.noop;
  }

  updateCompIdInPointersMap(structure, itemId, updatePointerMap);
  return _.mapValues(structure, function (value, property) {
    return _.invoke(uniquePropertyMapperFunctions, property, value, itemId, updatePointerMap) || value;
  });
}

function getUniqueFlatStructureMap(compsMap, itemId, updatePointerMap) {
  if (updatePointerMap === void 0) {
    updatePointerMap = _.noop;
  }

  return _(compsMap).map(function (comp) {
    return getUniqueStructure(comp, itemId, updatePointerMap);
  }).keyBy('id').value();
}

var originalPropertyMapperFunctions = {
  dataQuery: getRepeaterTemplateId,
  designQuery: getRepeaterTemplateId,
  id: getRepeaterTemplateId,
  layout: function layout(value) {
    return _.cloneDeep(value);
  },
  components: function components(value) {
    return _.map(value, function (child) {
      return child.id ? getOriginalStructure(child) : getRepeaterTemplateId(child);
    });
  }
};

function getOriginalStructure(itemStructure) {
  return _(itemStructure).omit('parent').mapValues(function (value, property) {
    return _.invoke(originalPropertyMapperFunctions, property, value) || value;
  }).value();
}

function unwrapDisplayedId(displayedId) {
  var repeatedItemId = getRepeaterItemId(displayedId);
  return {
    compId: repeatedItemId ? getRepeaterTemplateId(displayedId) : displayedId,
    wrappingFunction: repeatedItemId ? _.partialRight(getUniqueDisplayedId, repeatedItemId) : _.identity
  };
}

function createRefIdWithSuffix(refHostId, referredCompId, suffix) {
  if (suffix === void 0) {
    suffix = '';
  }

  var _unwrapDisplayedId = unwrapDisplayedId(referredCompId),
      compId = _unwrapDisplayedId.compId,
      wrappingFunction = _unwrapDisplayedId.wrappingFunction;

  var newReferredCompId = "" + getUniqueRefId(refHostId, compId) + suffix;
  return wrappingFunction(newReferredCompId);
}

var isRefPointer = function isRefPointer(p) {
  return p && isReferredId(p.id);
};

module.exports = {
  getUniqueFlatStructureMap: getUniqueFlatStructureMap,
  getUniqueStructure: getUniqueStructure,
  getOriginalStructure: getOriginalStructure,
  isRepeatedComponent: isRepeatedComponent,
  isDisplayedOnlyComponent: isDisplayedOnlyComponent,
  getRepeaterItemId: getRepeaterItemId,
  getRepeaterTemplateId: getRepeaterTemplateId,
  getUniqueDisplayedId: getUniqueDisplayedId,
  isRefPointer: isRefPointer,
  isReferredId: isReferredId,
  getReferredCompId: getReferredCompId,
  getRefHostCompId: getRefHostCompId,
  getRootRefHostCompId: getRootRefHostCompId,
  getUniqueRefId: getUniqueRefId,
  createRefIdWithSuffix: createRefIdWithSuffix,
  //Keeping these for backward compatibility while refactoring
  isDisplayedComponent: isRepeatedComponent,
  getItemId: getRepeaterItemId,
  getOriginalId: getRepeaterTemplateId
};
//# sourceMappingURL=displayedOnlyStructureUtil.js.map